<script setup lang="tsx">
import { computed } from 'vue'
import Multiselect from 'vue-multiselect'
import { riskConfig } from './riskConfig'
import type { RiskAxisLevel, RiskLevel } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import { formatAxisLevel, parseAxisLevel } from './shared'

const { t } = useI18n()

const props = defineProps<{
	class?: any
	type: 'impact' | 'probability'
}>()
const model = defineModel<number | undefined>({ required: true })
const options = computed(() => {
	if (props.type === 'impact') {
		return [{ title: t('misc.undefined') }, ...riskConfig.value.riskImpacts.filter((_, i) => i < riskConfig.value.matrixSize)]
	} else if (props.type === 'probability') {
		return [{ title: t('misc.undefined') }, ...riskConfig.value.riskProbabilities.filter((_, i) => i < riskConfig.value.matrixSize)]
	} else {
		return []
	}
})

const srvl = computed({
	get: () => {
		if (model.value === undefined) return

		const i = formatAxisLevel(model.value)
		if (props.type === 'impact') {
			return riskConfig.value.riskImpacts[i]
		} else if (props.type === 'probability') {
			return riskConfig.value.riskProbabilities[i]
		}
		return undefined
	},
	set: (a: RiskAxisLevel) => {
		let i = -1
		if (props.type === 'impact') {
			i = riskConfig.value.riskImpacts.indexOf(a)
		} else if (props.type === 'probability') {
			i = riskConfig.value.riskProbabilities.indexOf(a)
		}

		if (i >= 0) model.value = parseAxisLevel(i)
		else model.value = undefined
	},
})

function riskLevel(a: RiskAxisLevel): { level: RiskLevel | undefined; rank: number } {
	let i = -1
	if (props.type === 'impact') {
		i = riskConfig.value.riskImpacts.indexOf(a)
	} else if (props.type === 'probability') {
		i = riskConfig.value.riskProbabilities.indexOf(a)
	}

	if (i >= 0) return { level: riskConfig.value.riskLevels[i], rank: i + 1 }
	else return { level: undefined, rank: 0 }
}

let lvl: { level: RiskLevel | undefined; rank: number }
</script>

<template>
	<Multiselect
		:class="props.class"
		:searchable="false"
		:show-labels="false"
		:use-teleport="true"
		:allow-empty="false"
		:options="options"
		v-model="srvl"
		:placeholder="$t('misc.undefined')"
	>
		<template #option="{ option }">
			<div class="text-nowrap" v-one-tooltip="option.description" :set="(lvl = riskLevel(option))">
				<i v-if="lvl.level" class="bi bi-circle-fill" :style="'color: ' + lvl.level.color"></i>&nbsp;
				<span>
					<template v-if="lvl.rank > 0">
						{{ lvl.rank }}
					</template>
					{{ option.title }}
				</span>
			</div>
		</template>

		<template #singleLabel="{ option }">
			<div class="text-nowrap" v-one-tooltip="option.description" :set="(lvl = riskLevel(option))">
				<i v-if="lvl.level" class="bi bi-circle-fill" :style="'color: ' + lvl.level.color"></i>&nbsp;
				<span>
					<template v-if="lvl.rank > 0">
						{{ lvl.rank }}
					</template>
					{{ option.title }}
				</span>
			</div>
		</template>
	</Multiselect>
</template>
